/** /integration_presets/ — the registered ingestion presets (read-only).
 *
 * A preset is a named, versioned `tag_mapping` rule table shipped in the
 * backend's code, so a new tenant's tags are a configuration act rather than an
 * authoring act. There is deliberately no write API: presets may carry
 * `when`/`transform` callables, and a shipped one is immutable so that the same
 * ref always produces the same rules. A change ships as a new version.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet } from './client';

/** One rule as the backend chooses to describe it. Callables are reported by
 *  label ("strip surrounding whitespace"), never as code. */
export interface PresetRule {
  id: string;
  tag_category?: string;
  tag_context?: string;
  path?: string;
  /** Ordered alternatives; the first that matches wins (not a union). */
  sources?: string[];
  attributes?: { name?: string; path?: string; lang?: string }[];
  convergence?: string;
  /** Why this rule is standard — usually how many tenants chose it independently. */
  notes?: string;
  when?: string;
  transform?: string;
}

export interface IntegrationPreset {
  /** `<name>@<version>`, e.g. "ventrata-standard@1". */
  ref: string;
  name: string;
  version: number;
  restech: string;
  summary: string;
  /** Categories the integration emits with no rule at all (bokun's build_xml
   *  writes reseller/agent/channel before tag_mapping runs). Without this the
   *  preset would look like it is missing a mandatory reseller rule. */
  native_categories: string[];
  rules: PresetRule[];
}

export function list(restech?: string): Promise<{ results: IntegrationPreset[] }> {
  const qs = restech ? `?restech=${encodeURIComponent(restech)}` : '';
  return apiGet<{ results: IntegrationPreset[] }>(`/integration_presets/${qs}`);
}
